import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';
import { PagedResult, PaginationOptions } from '../shared/interfaces/pagination.interface';
import {
  Item,
  ItemCreate,
  ItemUpdate,
  UpdateItemQuantityRequest,
  MarkItemAsDamagedRequest,
  ItemsCountByStatus,
  ItemsCountByStock,
  ItemsCountByWarehouse,
  ItemsValueByStock,
  ItemsValueByWarehouse,
  ItemTransferRequest,
  ItemTransferResponse,
  ItemTransferHistory,
  ItemPrices,
  ItemPriceUpdate,
  PriceRangeFilter
} from '../models/Item';

export type ItemPagedResult = PagedResult<Item>;
export type ItemTransferHistoryPagedResult = PagedResult<ItemTransferHistory>;

@Injectable({
  providedIn: 'root'
})
export class ItemService {
  private apiUrl = `${environment.microservices.stock}/item`;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // ===== CRUD OPERATIONS =====

  createItem(item: ItemCreate): Observable<Item> {
    return this.http.post<Item>(this.apiUrl, item);
  }

  getItemById(id: number): Observable<Item> {
    return this.http.get<any>(`${this.apiUrl}/${id}`).pipe(
      map((dto) => this.mapItemFromDto(this.extractItemPayload(dto))),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar item');
        return throwError(() => error);
      })
    );
  }

  getItemByCode(code: string): Observable<Item> {
    return this.http.get<any>(`${this.apiUrl}/code/${code}`).pipe(
      map((dto) => this.mapItemFromDto(this.extractItemPayload(dto))),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar item por código');
        return throwError(() => error);
      })
    );
  }

  getAllItems(options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.apiUrl, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens');
        return throwError(() => error);
      })
    );
  }

  /**
   * Lista itens com filtro por nome via querystring:
   * GET /api/item?name=parafuso&page=1&pageSize=50
   */
  getAllItemsWithNameFilter(name: string, options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    const trimmedName = (name || '').trim();
    if (trimmedName) params = params.set('name', trimmedName);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(this.apiUrl, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens por nome');
        return throwError(() => error);
      })
    );
  }

  updateItem(id: number, item: ItemUpdate): Observable<Item> {
    return this.http.put<Item>(`${this.apiUrl}/${id}`, item);
  }

  deleteItem(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // ===== BASIC QUERIES =====

  getItemsByName(name: string, options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/name/${name}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens por nome');
        return throwError(() => error);
      })
    );
  }

  getActiveItems(options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/active`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens ativos');
        return throwError(() => error);
      })
    );
  }

  getItemsByStock(stockId: number, options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/stock/${stockId}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens por estoque');
        return throwError(() => error);
      })
    );
  }

  getItemsByStockWithSearch(stockId: number, name?: string, options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (name && name.trim()) params = params.set('name', name.trim());
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(`${this.apiUrl}/stock/${stockId}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao pesquisar itens dentro do estoque');
        return throwError(() => error);
      })
    );
  }

  getItemsByWarehouse(warehouseId: number, options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/warehouse/${warehouseId}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens por almoxarifado');
        return throwError(() => error);
      })
    );
  }

  getItemsByStatus(status: string, options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/status/${status}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens por status');
        return throwError(() => error);
      })
    );
  }

  // ===== ITEM SPECIFIC QUERIES =====

  getItemBySerialNumber(serialNumber: string): Observable<Item> {
    return this.http.get<Item>(`${this.apiUrl}/serial/${serialNumber}`);
  }

  getItemsByBatchNumber(batchNumber: string, options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/batch/${batchNumber}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens por lote');
        return throwError(() => error);
      })
    );
  }

  getItemsByManufacturer(manufacturerName: string, options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/manufacturer/${manufacturerName}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens por fabricante');
        return throwError(() => error);
      })
    );
  }

  // ===== QUANTITY AND AVAILABILITY =====

  getLowStockItems(options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/low-stock`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens com estoque baixo');
        return throwError(() => error);
      })
    );
  }

  getOutOfStockItems(options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/out-of-stock`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens sem estoque');
        return throwError(() => error);
      })
    );
  }

  getAvailableItems(options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/available`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens disponíveis');
        return throwError(() => error);
      })
    );
  }

  getReservedItems(options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/reserved`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens reservados');
        return throwError(() => error);
      })
    );
  }

  getDamagedItems(options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/damaged`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens danificados');
        return throwError(() => error);
      })
    );
  }

  // ===== EXPIRATION AND QUALITY =====

  getExpiredItems(options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/expired`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens vencidos');
        return throwError(() => error);
      })
    );
  }

  getNearExpirationItems(options?: PaginationOptions): Observable<ItemPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/near-expiration`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens próximos ao vencimento');
        return throwError(() => error);
      })
    );
  }

  // ===== FINANCIAL QUERIES =====

  getTotalItemsValue(): Observable<{ totalValue: number }> {
    return this.http.get<{ totalValue: number }>(`${this.apiUrl}/value/total`);
  }

  getTotalItemsValueByStock(stockId: number): Observable<{ stockId: number; totalValue: number }> {
    return this.http.get<{ stockId: number; totalValue: number }>(`${this.apiUrl}/value/stock/${stockId}`);
  }

  getTotalItemsValueByWarehouse(warehouseId: number): Observable<{ warehouseId: number; totalValue: number }> {
    return this.http.get<{ warehouseId: number; totalValue: number }>(`${this.apiUrl}/value/warehouse/${warehouseId}`);
  }

  // ===== INVENTORY MANAGEMENT =====

  updateItemQuantity(id: number, request: UpdateItemQuantityRequest): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.apiUrl}/${id}/quantity`, request);
  }


  markItemAsDamaged(id: number, request: MarkItemAsDamagedRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${id}/mark-damaged`, request);
  }

  markItemAsInUse(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${id}/mark-in-use`, {});
  }

  markItemAsAvailable(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${id}/mark-available`, {});
  }

  // ===== BARCODE AND IDENTIFICATION =====

  getItemByBarcode(barcode: string): Observable<Item> {
    return this.http.get<Item>(`${this.apiUrl}/barcode/${barcode}`);
  }

  getItemByQRCode(qrCode: string): Observable<Item> {
    return this.http.get<Item>(`${this.apiUrl}/qrcode/${qrCode}`);
  }

  // ===== STATISTICS AND REPORTS =====

  getItemsCountByStatus(): Observable<ItemsCountByStatus[]> {
    return this.http.get<ItemsCountByStatus[]>(`${this.apiUrl}/stats/status`);
  }

  getItemsCountByStock(): Observable<ItemsCountByStock[]> {
    return this.http.get<ItemsCountByStock[]>(`${this.apiUrl}/stats/stock`);
  }

  getItemsCountByWarehouse(): Observable<ItemsCountByWarehouse[]> {
    return this.http.get<ItemsCountByWarehouse[]>(`${this.apiUrl}/stats/warehouse`);
  }

  getItemsValueByStock(): Observable<ItemsValueByStock[]> {
    return this.http.get<ItemsValueByStock[]>(`${this.apiUrl}/stats/value/stock`);
  }

  getItemsValueByWarehouse(): Observable<ItemsValueByWarehouse[]> {
    return this.http.get<ItemsValueByWarehouse[]>(`${this.apiUrl}/stats/value/warehouse`);
  }

  // ===== ADDITIONAL STATISTICS FROM GUIDE =====

  getItemsCountByStatusStatistics(): Observable<{ [key: string]: number }> {
    return this.http.get<{ [key: string]: number }>(`${this.apiUrl}/statistics/count-by-status`);
  }

  getTotalItemsValueStatistics(): Observable<{ totalValue: number }> {
    return this.http.get<{ totalValue: number }>(`${this.apiUrl}/statistics/total-value`);
  }

  getItemsValueByStockStatistics(): Observable<{ stockId: number; stockName: string; totalValue: number }[]> {
    return this.http.get<{ stockId: number; stockName: string; totalValue: number }[]>(`${this.apiUrl}/statistics/value-by-stock`);
  }

  getItemsValueByWarehouseStatistics(): Observable<{ warehouseId: number; warehouseName: string; totalValue: number }[]> {
    return this.http.get<{ warehouseId: number; warehouseName: string; totalValue: number }[]>(`${this.apiUrl}/statistics/value-by-warehouse`);
  }

  // ===== UTILITY METHODS =====

  checkCodeExists(code: string, excludeId?: number): Observable<boolean> {
    let params = new HttpParams().set('code', code);
    if (excludeId) {
      params = params.set('excludeId', excludeId.toString());
    }
    return this.http.get<boolean>(`${this.apiUrl}/check-code`, { params });
  }

  checkSerialNumberExists(serialNumber: string, excludeId?: number): Observable<boolean> {
    let params = new HttpParams().set('serialNumber', serialNumber);
    if (excludeId) {
      params = params.set('excludeId', excludeId.toString());
    }
    return this.http.get<boolean>(`${this.apiUrl}/check-serial`, { params });
  }

  isItemAvailable(id: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/${id}/available`);
  }

  // ===== TRANSFER OPERATIONS =====

  transferItem(transferRequest: ItemTransferRequest): Observable<ItemTransferResponse> {
    return this.http.post<ItemTransferResponse>(`${this.apiUrl}/transfer`, transferRequest).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao transferir item');
        return throwError(() => error);
      })
    );
  }

  validateTransfer(transferRequest: ItemTransferRequest): Observable<{ isValid: boolean; validationErrors: string[]; message: string }> {
    return this.http.post<{ isValid: boolean; validationErrors: string[]; message: string }>(`${this.apiUrl}/transfer/validate`, transferRequest).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao validar transferência');
        return throwError(() => error);
      })
    );
  }

  getTransferHistory(itemId: number, options?: PaginationOptions): Observable<ItemTransferHistoryPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/${itemId}/transfer-history`, { params }).pipe(
      map(resp => this.mapTransferHistoryPagedResponse(resp)),
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar histórico de transferências');
        return throwError(() => error);
      })
    );
  }

  private mapPagedResponse(response: any): ItemPagedResult {
    const payload = this.extractPagedPayload(response);
    const source = payload ?? response;
    const toNumber = (value: any, fallback: number): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const rawItems = Array.isArray(source?.Items)
      ? source.Items
      : Array.isArray(source?.items)
        ? source.items
        : Array.isArray(source?.Data)
          ? source.Data
          : Array.isArray(source?.data)
            ? source.data
            : Array.isArray(source?.Result)
              ? source.Result
              : Array.isArray(source?.result)
                ? source.result
                : Array.isArray(source?.List)
                  ? source.List
                  : Array.isArray(source?.list)
                    ? source.list
                    : Array.isArray(source)
                      ? source
                      : [];

    const items = rawItems.map((item: any) => this.mapItemFromDto(item));

    const page = toNumber(source?.Page ?? source?.page, 1);
    const pageSize = toNumber(source?.PageSize ?? source?.pageSize, items.length);
    const totalItems = toNumber(source?.TotalItems ?? source?.totalItems ?? source?.TotalRecords ?? source?.totalRecords, items.length);
    const totalPages = toNumber(source?.TotalPages ?? source?.totalPages, (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1));

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }

  // ===== PRICE ENDPOINTS =====

  /**
   * Obtém os preços de um item específico
   * @param id ID do item
   * @returns Observable<ItemPrices>
   */
  getItemPrices(id: number): Observable<ItemPrices> {
    return this.http.get<ItemPrices>(`${this.apiUrl}/${id}/prices`).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar preços do item');
        return throwError(() => error);
      })
    );
  }

  /**
   * Atualiza apenas os campos de preço de um item
   * @param id ID do item
   * @param priceData Dados de preço a serem atualizados
   * @returns Observable<Item>
   */
  updateItemPrices(id: number, priceData: ItemPriceUpdate): Observable<Item> {
    return this.http.put<Item>(`${this.apiUrl}/${id}/prices`, priceData).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao atualizar preços do item');
        return throwError(() => error);
      })
    );
  }

  /**
   * Lista todos os itens incluindo informações de preço
   * @returns Observable<Item[]>
   */
  getAllItemsWithPrices(): Observable<Item[]> {
    return this.http.get<Item[]>(`${this.apiUrl}/prices`).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens com preços');
        return throwError(() => error);
      })
    );
  }

  /**
   * Busca itens por faixa de preço usando filtros
   * @param filters Filtros de faixa de preço
   * @returns Observable<Item[]>
   */
  getItemsByPriceRange(filters: PriceRangeFilter): Observable<Item[]> {
    let params = new HttpParams();

    if (filters.minUnitCost !== undefined) {
      params = params.set('minUnitCost', filters.minUnitCost.toString());
    }
    if (filters.maxUnitCost !== undefined) {
      params = params.set('maxUnitCost', filters.maxUnitCost.toString());
    }
    if (filters.minUnitPrice !== undefined) {
      params = params.set('minUnitPrice', filters.minUnitPrice.toString());
    }
    if (filters.maxUnitPrice !== undefined) {
      params = params.set('maxUnitPrice', filters.maxUnitPrice.toString());
    }
    if (filters.minCostPrice !== undefined) {
      params = params.set('minCostPrice', filters.minCostPrice.toString());
    }
    if (filters.maxCostPrice !== undefined) {
      params = params.set('maxCostPrice', filters.maxCostPrice.toString());
    }
    if (filters.minSalePrice !== undefined) {
      params = params.set('minSalePrice', filters.minSalePrice.toString());
    }
    if (filters.maxSalePrice !== undefined) {
      params = params.set('maxSalePrice', filters.maxSalePrice.toString());
    }

    return this.http.get<Item[]>(`${this.apiUrl}/prices/range`, { params }).pipe(
      catchError(error => {
        this.errorService.showErrorMessage('Erro ao buscar itens por faixa de preço');
        return throwError(() => error);
      })
    );
  }

  private mapTransferHistoryPagedResponse(response: any): ItemTransferHistoryPagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : [];

    // ItemTransferHistory já vem mapeado do backend, não precisa de transformação
    const items = rawItems as ItemTransferHistory[];
    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return { items, page, pageSize, totalItems, totalPages };
  }

  private mapItemFromDto(dto: any): Item {
    const pick = (obj: any, keys: string[]) => keys.map((k) => obj?.[k]).find((v) => v !== undefined);
    const toNumber = (value: any): number | undefined => {
      if (value === null || value === undefined || value === '') return undefined;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? undefined : parsed;
    };
    const toBoolean = (value: any, fallback = false): boolean => {
      if (value === null || value === undefined || value === '') return fallback;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') return value.toLowerCase() === 'true';
      return Boolean(value);
    };

    return {
      id: toNumber(pick(dto, ['id', 'Id'])) ?? 0,
      code: pick(dto, ['code', 'Code']) ?? '',
      name: pick(dto, ['name', 'Name']) ?? '',
      description: pick(dto, ['description', 'Description']),
      stockId: toNumber(pick(dto, ['stockId', 'StockId'])) ?? 0,
      warehouseId: toNumber(pick(dto, ['warehouseId', 'WarehouseId'])),
      serialNumber: pick(dto, ['serialNumber', 'SerialNumber']),
      batchNumber: pick(dto, ['batchNumber', 'BatchNumber']),
      manufacturerCode: pick(dto, ['manufacturerCode', 'ManufacturerCode']),
      manufacturerName: pick(dto, ['manufacturerName', 'ManufacturerName']),
      model: pick(dto, ['model', 'Model']),
      partNumber: pick(dto, ['partNumber', 'PartNumber']),
      unit: pick(dto, ['unit', 'Unit']),
      weight: toNumber(pick(dto, ['weight', 'Weight'])),
      volume: toNumber(pick(dto, ['volume', 'Volume'])),
      length: toNumber(pick(dto, ['length', 'Length'])),
      width: toNumber(pick(dto, ['width', 'Width'])),
      height: toNumber(pick(dto, ['height', 'Height'])),
      specificLocation: pick(dto, ['specificLocation', 'SpecificLocation']),
      shelf: pick(dto, ['shelf', 'Shelf']),
      rack: pick(dto, ['rack', 'Rack']),
      position: pick(dto, ['position', 'Position']),
      quantity: toNumber(pick(dto, ['quantity', 'Quantity'])) ?? 0,
      minimumQuantity: toNumber(pick(dto, ['minimumQuantity', 'MinimumQuantity'])),
      maximumQuantity: toNumber(pick(dto, ['maximumQuantity', 'MaximumQuantity'])),
      manufactureDate: pick(dto, ['manufactureDate', 'ManufactureDate']),
      expirationDate: pick(dto, ['expirationDate', 'ExpirationDate']),
      bestBeforeDate: pick(dto, ['bestBeforeDate', 'BestBeforeDate']),
      qualityStatus: pick(dto, ['qualityStatus', 'QualityStatus']),
      unitCost: toNumber(pick(dto, ['unitCost', 'UnitCost'])),
      totalCost: toNumber(pick(dto, ['totalCost', 'TotalCost'])),
      unitPrice: toNumber(pick(dto, ['unitPrice', 'UnitPrice'])),
      totalPrice: toNumber(pick(dto, ['totalPrice', 'TotalPrice'])),
      currencyCode: pick(dto, ['currencyCode', 'CurrencyCode']),
      costPrice: toNumber(pick(dto, ['costPrice', 'CostPrice'])),
      salePrice: toNumber(pick(dto, ['salePrice', 'SalePrice'])),
      approvalValueLimit: toNumber(pick(dto, ['approvalValueLimit', 'ApprovalValueLimit'])),
      requiresApproval: toBoolean(pick(dto, ['requiresApproval', 'RequiresApproval']), false),
      status: pick(dto, ['status', 'Status']) ?? 'Disponível',
      condition: pick(dto, ['condition', 'Condition']),
      isActive: toBoolean(pick(dto, ['isActive', 'IsActive']), true),
      isInUse: toBoolean(pick(dto, ['isInUse', 'IsInUse']), false),
      isDamaged: toBoolean(pick(dto, ['isDamaged', 'IsDamaged']), false),
      lastMovementDate: pick(dto, ['lastMovementDate', 'LastMovementDate']),
      lastInventoryDate: pick(dto, ['lastInventoryDate', 'LastInventoryDate']),
      lastMovementType: pick(dto, ['lastMovementType', 'LastMovementType']),
      lastMovementByStaffId: toNumber(pick(dto, ['lastMovementByStaffId', 'LastMovementByStaffId'])),
      color: pick(dto, ['color', 'Color']),
      size: pick(dto, ['size', 'Size']),
      supplierReference: pick(dto, ['supplierReference', 'SupplierReference']),
      notes: pick(dto, ['notes', 'Notes']),
      barcode: pick(dto, ['barcode', 'Barcode']),
      qrCode: pick(dto, ['qrCode', 'QrCode']),
      createdAt: pick(dto, ['createdAt', 'CreatedAt']),
      updatedAt: pick(dto, ['updatedAt', 'UpdatedAt']),
      createdBy: pick(dto, ['createdBy', 'CreatedBy']),
      updatedBy: pick(dto, ['updatedBy', 'UpdatedBy']),
      stockName: pick(dto, ['stockName', 'StockName']),
      warehouseName: pick(dto, ['warehouseName', 'WarehouseName']),
      stockCode: pick(dto, ['stockCode', 'StockCode']),
      warehouseCode: pick(dto, ['warehouseCode', 'WarehouseCode']),
      lastMovementByStaffName: pick(dto, ['lastMovementByStaffName', 'LastMovementByStaffName']),
      isExpired: toBoolean(pick(dto, ['isExpired', 'IsExpired']), false),
      isNearExpiration: toBoolean(pick(dto, ['isNearExpiration', 'IsNearExpiration']), false),
      isLowStock: toBoolean(pick(dto, ['isLowStock', 'IsLowStock']), false),
      daysToExpiration: toNumber(pick(dto, ['daysToExpiration', 'DaysToExpiration']))
    };
  }

  private extractItemPayload(response: any): any {
    if (!response || typeof response !== 'object') {
      return response;
    }

    let current = response;

    while (current && typeof current === 'object') {
      const next =
        current.Item
        ?? current.item
        ?? current.Data
        ?? current.data
        ?? current.Result
        ?? current.result
        ?? current.value;

      if (!next || next === current) {
        break;
      }

      current = next;
    }

    return current;
  }

  private extractPagedPayload(response: any): any {
    if (!response || typeof response !== 'object') {
      return response;
    }

    const candidates = [
      response,
      response.Data,
      response.data,
      response.Result,
      response.result,
      response.Value,
      response.value
    ];

    return candidates.find((candidate) =>
      candidate &&
      typeof candidate === 'object' &&
      (
        Array.isArray(candidate?.Items) ||
        Array.isArray(candidate?.items) ||
        Array.isArray(candidate?.Data) ||
        Array.isArray(candidate?.data) ||
        Array.isArray(candidate?.Result) ||
        Array.isArray(candidate?.result) ||
        Array.isArray(candidate?.List) ||
        Array.isArray(candidate?.list) ||
        candidate?.TotalItems !== undefined ||
        candidate?.totalItems !== undefined ||
        candidate?.TotalRecords !== undefined ||
        candidate?.totalRecords !== undefined
      )
    ) ?? response;
  }
}
